import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Chip,
  MenuItem,
} from '@mui/material';
import { pricingImportAPI } from '../../services/api';
import type { PriceImportDiffRow, DbEditionImportDiffRow } from '../../types';

interface ImportProvider {
  ProviderId: number;
  ProviderName: string;
  importMode: 'vm' | 'dbEdition';
}

interface ImportRegion {
  RegionId: number;
  RegionName: string;
  RegionCode: string;
  ExternalRegion?: string;
}

const formatCurrency = (val: number | null): string => {
  if (val === null) return '-';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PriceImport: React.FC = () => {
  const [providers, setProviders] = useState<ImportProvider[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [regions, setRegions] = useState<ImportRegion[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<number | ''>('');

  // VM mode state
  const [vmDiffs, setVmDiffs] = useState<PriceImportDiffRow[]>([]);
  // DB Edition mode state
  const [dbEditionDiffs, setDbEditionDiffs] = useState<DbEditionImportDiffRow[]>([]);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [applying, setApplying] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load both provider lists and merge (handle each independently so one failure doesn't break the other)
  useEffect(() => {
    setLoading(true);
    const vmPromise = pricingImportAPI.getProviders()
      .then(res => (res.data as Array<{ ProviderId: number; ProviderName: string }>).map(p => ({
        ...p,
        importMode: 'vm' as const,
      })))
      .catch(() => [] as ImportProvider[]);

    const dbPromise = pricingImportAPI.getDbEditionProviders()
      .then(res => (res.data as Array<{ ProviderId: number; ProviderName: string }>).map(p => ({
        ...p,
        importMode: 'dbEdition' as const,
      })))
      .catch(() => [] as ImportProvider[]);

    Promise.all([vmPromise, dbPromise]).then(([vmProviders, dbProviders]) => {
      setProviders([...vmProviders, ...dbProviders]);
    }).finally(() => setLoading(false));
  }, []);

  const selectedProvider = providers[selectedTab];
  const mode = selectedProvider?.importMode || 'vm';

  const clearDiffs = () => {
    setVmDiffs([]);
    setDbEditionDiffs([]);
    setChecked(new Set());
  };

  const fetchRegions = useCallback(async (provider: ImportProvider) => {
    setRegions([]);
    setSelectedRegionId('');
    clearDiffs();
    try {
      const res = provider.importMode === 'vm'
        ? await pricingImportAPI.getRegions(provider.ProviderId)
        : await pricingImportAPI.getDbEditionRegions(provider.ProviderId);
      setRegions(res.data as ImportRegion[]);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load regions' });
    }
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      fetchRegions(selectedProvider);
    }
  }, [selectedProvider, fetchRegions]);

  // Diff items for the current mode
  const diffCount = mode === 'vm' ? vmDiffs.length : dbEditionDiffs.length;

  // ===== VM mode handlers =====
  const handleFetchVmPrices = async () => {
    if (!selectedProvider || !selectedRegionId) return;
    setFetching(true);
    setAlert(null);
    clearDiffs();

    try {
      const res = await pricingImportAPI.fetchPrices(selectedProvider.ProviderId, selectedRegionId as number);
      const data = res.data as PriceImportDiffRow[];
      setVmDiffs(data);
      setChecked(new Set(data.map(d => `${d.vmTypeId}|${d.osCategory}`)));
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to fetch cloud prices';
      setAlert({ type: 'error', message: msg });
    } finally {
      setFetching(false);
    }
  };

  const handleApplyVm = async () => {
    if (!selectedProvider || !selectedRegionId) return;
    setApplying(true);
    setAlert(null);

    const updates = vmDiffs
      .filter(d => checked.has(`${d.vmTypeId}|${d.osCategory}`))
      .map(d => ({
        vmTypeId: d.vmTypeId,
        osCategory: d.osCategory,
        monthlyCost: d.apiMonthlyCost,
      }));

    try {
      const res = await pricingImportAPI.applyPrices(
        selectedProvider.ProviderId,
        selectedRegionId as number,
        updates
      );
      setAlert({ type: 'success', message: `Successfully imported ${res.data.count} VM price(s)${res.data.dbEditionCount ? ` and updated ${res.data.dbEditionCount} database edition price(s)` : ''}` });
      clearDiffs();
    } catch {
      setAlert({ type: 'error', message: 'Failed to apply price updates' });
    } finally {
      setApplying(false);
    }
  };

  // ===== DB Edition mode handlers =====
  const handleFetchDbEditionPrices = async () => {
    if (!selectedProvider || !selectedRegionId) return;
    setFetching(true);
    setAlert(null);
    clearDiffs();

    try {
      const res = await pricingImportAPI.fetchDbEditionPrices(selectedProvider.ProviderId, selectedRegionId as number);
      const data = res.data as DbEditionImportDiffRow[];
      setDbEditionDiffs(data);
      setChecked(new Set(data.map(d => `${d.vmTypeId}|${d.databaseEditionId}`)));
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to calculate database edition prices';
      setAlert({ type: 'error', message: msg });
    } finally {
      setFetching(false);
    }
  };

  const handleApplyDbEdition = async () => {
    if (!selectedProvider || !selectedRegionId) return;
    setApplying(true);
    setAlert(null);

    const updates = dbEditionDiffs
      .filter(d => checked.has(`${d.vmTypeId}|${d.databaseEditionId}`))
      .map(d => ({
        vmTypeId: d.vmTypeId,
        databaseEditionId: d.databaseEditionId,
        monthlyCost: d.calculatedMonthlyCost,
      }));

    try {
      const res = await pricingImportAPI.applyDbEditionPrices(
        selectedProvider.ProviderId,
        selectedRegionId as number,
        updates
      );
      setAlert({ type: 'success', message: `Successfully updated ${res.data.count} database edition price(s)` });
      clearDiffs();
    } catch {
      setAlert({ type: 'error', message: 'Failed to apply database edition price updates' });
    } finally {
      setApplying(false);
    }
  };

  // ===== Shared handlers =====
  const handleFetch = mode === 'vm' ? handleFetchVmPrices : handleFetchDbEditionPrices;
  const handleApply = mode === 'vm' ? handleApplyVm : handleApplyDbEdition;

  const handleToggle = (key: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (checked.size === diffCount) {
      setChecked(new Set());
    } else if (mode === 'vm') {
      setChecked(new Set(vmDiffs.map(d => `${d.vmTypeId}|${d.osCategory}`)));
    } else {
      setChecked(new Set(dbEditionDiffs.map(d => `${d.vmTypeId}|${d.databaseEditionId}`)));
    }
  };

  const getDeltaColor = (delta: number | null): string => {
    if (delta === null) return '#9e9e9e';
    if (delta < 0) return '#2e7d32';
    if (delta > 0) return '#d32f2f';
    return 'inherit';
  };

  const getDeltaText = (delta: number | null): string => {
    if (delta === null) return 'New';
    if (delta === 0) return '$0.00';
    const sign = delta > 0 ? '+' : '';
    return `${sign}${formatCurrency(delta)}`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Price Import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {mode === 'vm'
          ? 'Fetch VM pricing from cloud provider APIs and compare against current pricing. Select rows to import.'
          : 'Calculate Database on VM pricing from existing Windows VM prices and SQL license rates. Select rows to apply.'}
      </Typography>

      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : providers.length === 0 ? (
        <Alert severity="info">
          No providers are configured for price import. Set up SKU/region mappings or SQL license rates in API Mappings first.
        </Alert>
      ) : (
        <>
          <Tabs value={selectedTab} onChange={(_, v) => { setSelectedTab(v); clearDiffs(); }} sx={{ mb: 2 }}>
            {providers.map(p => (
              <Tab
                key={`${p.ProviderId}-${p.importMode}`}
                label={p.importMode === 'dbEdition' ? `${p.ProviderName} (DB on VM)` : p.ProviderName}
              />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <TextField
              select
              label="Region"
              value={selectedRegionId}
              onChange={e => { setSelectedRegionId(Number(e.target.value)); clearDiffs(); }}
              sx={{ minWidth: 250 }}
              size="small"
            >
              {regions.map(r => (
                <MenuItem key={r.RegionId} value={r.RegionId}>
                  {r.RegionName}{r.ExternalRegion ? ` (${r.ExternalRegion})` : ` (${r.RegionCode})`}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={!selectedRegionId || fetching}
            >
              {fetching
                ? 'Calculating...'
                : mode === 'vm' ? 'Fetch Prices' : 'Calculate DB Edition Prices'}
            </Button>
          </Box>

          {fetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* ===== VM Mode Diff Table ===== */}
          {mode === 'vm' && vmDiffs.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Button size="small" variant="outlined" onClick={handleSelectAll}>
                  {checked.size === diffCount ? 'Deselect All' : 'Select All'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {checked.size} of {diffCount} selected
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked.size === diffCount}
                          indeterminate={checked.size > 0 && checked.size < diffCount}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>VM Type</TableCell>
                      <TableCell>CPU</TableCell>
                      <TableCell>RAM (GB)</TableCell>
                      <TableCell>OS Category</TableCell>
                      <TableCell align="right">Current Price</TableCell>
                      <TableCell align="right">API Price</TableCell>
                      <TableCell align="right">Delta</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vmDiffs.map(d => {
                      const key = `${d.vmTypeId}|${d.osCategory}`;
                      return (
                        <TableRow key={key} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked.has(key)}
                              onChange={() => handleToggle(key)}
                            />
                          </TableCell>
                          <TableCell>{d.vmName}</TableCell>
                          <TableCell>{d.cpu}</TableCell>
                          <TableCell>{d.ram}</TableCell>
                          <TableCell>
                            <Chip
                              label={d.osCategory}
                              size="small"
                              color={d.osCategory === 'Windows' ? 'primary' : d.osCategory === 'LinuxPaid' ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(d.currentMonthlyCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(d.apiMonthlyCost)}</TableCell>
                          <TableCell align="right" sx={{ color: getDeltaColor(d.delta), fontWeight: 'bold' }}>
                            {getDeltaText(d.delta)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                variant="contained"
                color="primary"
                onClick={handleApply}
                disabled={checked.size === 0 || applying}
              >
                {applying ? 'Applying...' : `Apply Selected (${checked.size})`}
              </Button>
            </>
          )}

          {/* ===== DB Edition Mode Diff Table ===== */}
          {mode === 'dbEdition' && dbEditionDiffs.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Button size="small" variant="outlined" onClick={handleSelectAll}>
                  {checked.size === diffCount ? 'Deselect All' : 'Select All'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {checked.size} of {diffCount} selected
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked.size === diffCount}
                          indeterminate={checked.size > 0 && checked.size < diffCount}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>VM Type</TableCell>
                      <TableCell>CPU</TableCell>
                      <TableCell>RAM (GB)</TableCell>
                      <TableCell>Edition</TableCell>
                      <TableCell align="right">Current Price</TableCell>
                      <TableCell align="right">Calculated Price</TableCell>
                      <TableCell align="right">Delta</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dbEditionDiffs.map(d => {
                      const key = `${d.vmTypeId}|${d.databaseEditionId}`;
                      return (
                        <TableRow key={key} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked.has(key)}
                              onChange={() => handleToggle(key)}
                            />
                          </TableCell>
                          <TableCell>{d.vmName}</TableCell>
                          <TableCell>{d.cpu}</TableCell>
                          <TableCell>{d.ram}</TableCell>
                          <TableCell>
                            <Chip
                              label={d.editionName}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(d.currentMonthlyCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(d.calculatedMonthlyCost)}</TableCell>
                          <TableCell align="right" sx={{ color: getDeltaColor(d.delta), fontWeight: 'bold' }}>
                            {getDeltaText(d.delta)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                variant="contained"
                color="primary"
                onClick={handleApply}
                disabled={checked.size === 0 || applying}
              >
                {applying ? 'Applying...' : `Apply Selected (${checked.size})`}
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PriceImport;
